import type { ThresholdRanges } from "@/libs/cv/validation"
import { ThresholdsValidation, Validation, type ValidationContext } from "@/libs/cv/validation"
import { normalize } from "@/libs/math"
import {
  createFacePitchAngleDeviationValidation,
  createFaceRollAngleDeviationValidation,
  createFaceYawAngleDeviationValidation,
  type FaceValidationContext,
} from "../validation"
import type { Detections } from "./preset"
import { PROBLEM } from "./stages"
import { getLightingAzimuthThresholds, getLightingIntensityThresholds } from "./validation"

export type { ThresholdRanges }

type ImageValidationContext = ValidationContext<{
  face: Detections["face"]
  illumination: Detections["illumination"]
  quality: Detections["quality"]
}>

type ValidationRulesOptions = {
  lightingDirectionCoeff: number
  lightingIntensityCoeff: number
  blurQualityCheckEnabled: boolean
  noiseQualityCheckEnabled: boolean
  faceAngleDeviationCheckEnabled: boolean
  fullFaceVisibleCheckEnabled: boolean
}

// Optimal threshold is 556.48, but it is too strict for real-world images with faces, let's try to substract ~11% from it
// After manual testing, the threshold of 498.37 seems to be a good compromise.
const BLUR_THRESHOLD = 448.533

/**
 * After manual QA threshold >= 8 seems to be a good compromise for real-world high-resolution images with faces.
 */
const NOISE_THRESHOLD = 9

/**
 * Creates the set of validation rules for image processing.
 * Included validations:
 * - Face detection (FACE_DISPLACED)
 * - Incorrect angle: pitch, roll, yaw deviations
 * - Poor lighting: insufficient intensity and incorrect lighting angle
 * - Quality: noise, blur, resolution, and full-face visibility
 */
const createValidationRules = ({
  lightingDirectionCoeff,
  lightingIntensityCoeff,
  blurQualityCheckEnabled,
  noiseQualityCheckEnabled,
  faceAngleDeviationCheckEnabled,
  fullFaceVisibleCheckEnabled,
}: ValidationRulesOptions) => {
  const lightingAzimuthThresholds = getLightingAzimuthThresholds(lightingDirectionCoeff)
  const lightingIntensityThresholds = getLightingIntensityThresholds(lightingIntensityCoeff)

  const faceAngleDeviationThresholds: ThresholdRanges = faceAngleDeviationCheckEnabled
    ? { good: [-15, 15], perfect: [-15, 15] }
    : { good: [-Infinity, Infinity], perfect: [-Infinity, Infinity] }

  const pitchThresholds: ThresholdRanges = faceAngleDeviationThresholds
  const yawThresholds: ThresholdRanges = faceAngleDeviationThresholds
  const rollThresholds: ThresholdRanges = faceAngleDeviationThresholds

  const rules = {
    [PROBLEM.FACE_DISPLACED]: [
      Validation<FaceValidationContext, Detections["face"]>({
        value: ({ detections }) => detections.face,
        validate: (detection) => detection !== null,
      }),
    ],
    [PROBLEM.FACE_PITCH_ANGLE_DEVIATION]: createFacePitchAngleDeviationValidation(pitchThresholds),
    /** Arises when the face rotations around Z axis is deviated from the target one */
    [PROBLEM.FACE_ROLL_ANGLE_DEVIATION]: createFaceRollAngleDeviationValidation(rollThresholds),
    /** Arises when the face rotations around Y axis is deviated from the target one */
    [PROBLEM.FACE_YAW_ANGLE_DEVIATION]: createFaceYawAngleDeviationValidation(yawThresholds),
    [PROBLEM.LIGHTING_INSUFFICIENT]: [
      ThresholdsValidation<ImageValidationContext>({
        value: ({ detections }) => detections.illumination.intensity,
        thresholds: lightingIntensityThresholds,
      }),
    ],
    [PROBLEM.LIGHTING_EXCESSIVE]: [],
    [PROBLEM.LIGHTING_ANGLE_DEVIATION]: [
      ThresholdsValidation<ImageValidationContext>({
        value: ({ detections }) => detections.illumination.azimuth,
        thresholds: lightingAzimuthThresholds,
      }),
      ThresholdsValidation<ImageValidationContext>({
        value: ({ detections }) => detections.illumination.elevation,
        thresholds: {
          good: normalize([-30, 45], 90),
          perfect: normalize([-30, 45], 90),
        },
      }),
    ],
    [PROBLEM.QUALITY_NOISE]: noiseQualityCheckEnabled
      ? [
          ThresholdsValidation<ImageValidationContext>({
            value: ({ detections }) => detections.quality.noiseScore,
            thresholds: { good: [0, NOISE_THRESHOLD], perfect: [0, NOISE_THRESHOLD] },
          }),
        ]
      : [],
    [PROBLEM.QUALITY_BLUR]: blurQualityCheckEnabled
      ? [
          ThresholdsValidation<ImageValidationContext>({
            value: ({ detections }) => detections.quality.blurScore,
            thresholds: { good: [BLUR_THRESHOLD, Infinity], perfect: [BLUR_THRESHOLD, Infinity] },
          }),
        ]
      : [],
    [PROBLEM.QUALITY_RESOLUTION]: [
      ThresholdsValidation<ImageValidationContext>({
        value: ({ detections }) => detections.quality.resolutionScore,
        thresholds: { good: [1, Infinity], perfect: [1, Infinity] },
      }),
    ],
    [PROBLEM.QUALITY_FULL_FACE]: fullFaceVisibleCheckEnabled
      ? [
          Validation<ImageValidationContext, boolean>({
            value: ({ detections }) => detections.quality.fullFaceVisible,
            validate: (visible) => visible,
          }),
        ]
      : [],
  }

  return {
    rules,
    thresholds: {
      lightingDirection: lightingAzimuthThresholds,
      lightingIntensity: lightingIntensityThresholds,
      pitch: pitchThresholds,
      yaw: yawThresholds,
    },
  }
}

/**
 * Creates the image processing validator.
 * It first checks face detection; if no face is found, returns FACE_DISPLACED.
 * Otherwise, it validates all other PROBLEMs and returns the set of failures,
 * or WELL_DONE if all validations pass.
 */
export function createImageValidator(options: ValidationRulesOptions) {
  const { rules } = createValidationRules(options)
  const rulesEntries = Object.entries(rules)

  function validate(ctx: ImageValidationContext): Set<PROBLEM> {
    const validationResult: Set<PROBLEM> = new Set()
    if (!ctx.detections.face) return validationResult.add(PROBLEM.FACE_DISPLACED)

    for (const [name, rules] of rulesEntries) {
      if (name === PROBLEM.FACE_DISPLACED) continue

      for (const rule of rules) {
        /// FIXME: Haven't found a way to make a acceptable type conversion here
        if (!rule.validate(rule.value(ctx) as never, ctx)) {
          validationResult.add(name as PROBLEM)
          break
        }
      }
    }

    return validationResult
  }

  return { validate }
}
